/**
 * The messages on the phone website (web-account-pages D2, D8; phone-signed-in/messages.html,
 * messages-secure-filter.html and q1-both.html): the app's own chat screen (ClientChatView,
 * shared-client-screens D3) showing the one stream from both rooms, fed by `useWebChatThread`.
 * `openMessages()` opens it over the page as a history entry of its own (the same address with a
 * marker in its state), so the browser's Back, Android's included, closes it there; the screen's
 * back button goes through the same Back, so the two never drift.
 *
 * The slots it fills (ruled 2026-10-22, the review's Keep rows M1 to M5, M7, M11, M19 to M21):
 * over the page in a dialog of its own; Secure · Telegram under the studio's name; the filter row
 * All, Secure, Telegram, Chat, Bookings, Forms, Payments in the fold the app's tabs sit in; a lock
 * or the Telegram plane beside every time; one composer, which sends a secure message; opened at
 * a banner's message; links in a notice open a new tab; a notice's door opens the website page.
 */
import { useMemo, useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useChatThreadGate, type SavedPlace } from '@/hooks/chat/useChatThreadScroll';
import { browserTimezone, CHAT_FILTER_LABELS, chatEmptyState } from '@/lib/client/clientChatThread';
import { ClientChatView } from '@/pages/ClientChatScreen/ClientChatView';

import { useSiteStudio } from '../api/site';
import { useWebSession } from '../api/session';
import { webDoorHref } from '../messages/messageDoors';
import { WEB_MESSAGE_FILTERS, type WebMessageFilter } from '../messages/messageFilters';
import { useMessagesPanel } from '../messages/panelContext';
import { useWebChatThread } from '../messages/useWebChatThread';
import css from './PhoneSignedIn.module.css';

/** A link inside a notice opens in a new tab, never in place of the site. */
const openInNewTab = (href: string) => {
  window.open(href, '_blank', 'noopener,noreferrer');
};

/** Where the client was in the messages when they followed a notice's link (row M22). */
interface MessagesReturn {
  readonly filter: WebMessageFilter;
  readonly place: SavedPlace<WebMessageFilter>;
}

interface MessagesScreenProps {
  readonly filter: WebMessageFilter;
  readonly onFilter: (filter: WebMessageFilter) => void;
  readonly restore: SavedPlace<WebMessageFilter> | null;
  readonly focusId: string | null;
  readonly onBack: () => void;
  readonly openPage: (href: string, back: MessagesReturn) => void;
}

function MessagesScreen({ filter, onFilter, restore, focusId, onBack, openPage }: MessagesScreenProps) {
  const gate = useChatThreadGate();
  const thread = useWebChatThread(filter, gate);
  const studioName = useSiteStudio().data?.name ?? '';
  const session = useWebSession();
  const timezone = useMemo(() => browserTimezone(), []);
  const empty = thread.status === 'ready' && thread.entries.length === 0 ? chatEmptyState(filter) : null;

  return (
    <div className={css.messages} role="dialog" aria-modal="true" aria-label={`Messages with ${studioName || 'the business'}`}>
      <ClientChatView
        title={studioName}
        subline="rooms"
        roomSwitch={{
          kind: 'chips',
          label: 'Filter messages',
          chips: WEB_MESSAGE_FILTERS.map((id) => ({ id, label: CHAT_FILTER_LABELS[id], current: id === filter })),
          onChoose: onFilter,
        }}
        readOnly={false}
        status={thread.status}
        onRetry={thread.retry}
        empty={empty}
        entries={thread.entries}
        timezone={timezone}
        secureOf={thread.secureOf}
        markOf={thread.markOf}
        issueOf={thread.issueOf}
        onRetrySend={thread.retrySend}
        onDoor={(door, place) => {
          const href = webDoorHref(door);
          if (href) openPage(href, { filter, place });
        }}
        openLink={openInNewTab}
        composer={thread.status === 'not-found' ? null : {
          draftScope: `web:${session.user?.id ?? ''}`,
          placeholder: studioName ? `Message ${studioName}` : 'Message',
          onSend: thread.send,
          onType: thread.notifyTyping,
        }}
        typing={thread.typing}
        name={studioName}
        onBack={onBack}
        gate={gate}
        hasOlder={thread.hasOlder}
        loadOlder={thread.loadOlder}
        room="secure"
        lens={filter}
        restore={restore}
        focusId={focusId}
        testId="phone-messages"
      />
    </div>
  );
}

/** The history entry the open messages stand on: the page's own address and state, plus this marker. */
const MARKER = 'phoneMessages';
/** On that entry, where the client was when a notice's link took them to another page. */
const RETURN = 'phoneMessagesReturn';

/**
 * Ties the open messages to a history entry. Opening pushes the entry; leaving it (Back) closes
 * them; Back on the screen is the browser's Back. A notice's page records the filter and the place
 * on that entry, then opens on top of it, so Back from the page (the browser's, or a page's own
 * Back) lands on the entry and opens the messages again where the client left them, as the app's
 * chat does (rows M22, F2).
 */
function useMessagesHistory(): { back: () => void; openPage: (href: string, back: MessagesReturn) => void } {
  const navigate = useNavigate();
  const location = useLocation();
  const { open, openMessages, closeMessages } = useMessagesPanel();
  const state = (location.state as Record<string, unknown> | null) ?? null;
  const marked = state?.[MARKER] === true;
  const returnTo = marked ? (state?.[RETURN] as MessagesReturn | undefined) ?? null : null;
  // idle → pushing (the entry asked for) → shown (the entry is current) → idle.
  const phase = useRef<'idle' | 'pushing' | 'shown'>('idle');

  useEffect(() => {
    if (open && phase.current === 'idle') {
      phase.current = 'pushing';
      navigate(`${location.pathname}${location.search}${location.hash}`, { state: { ...(state ?? {}), [MARKER]: true } });
    } else if (!open && phase.current === 'idle' && returnTo) {
      // Back from a notice's page onto the messages' entry: they open where the client left them.
      phase.current = 'shown';
      openMessages({ filter: returnTo.filter, restore: returnTo.place });
    } else if (phase.current === 'pushing' && marked) {
      phase.current = 'shown';
    } else if (phase.current === 'shown' && !marked) {
      phase.current = 'idle';
      if (open) closeMessages();
    } else if (phase.current === 'shown' && !open) {
      phase.current = 'idle';
    }
  }, [open, marked, returnTo, state, location, navigate, openMessages, closeMessages]);

  return useMemo(
    () => ({
      back: () => navigate(-1),
      openPage: (href: string, back: MessagesReturn) => {
        phase.current = 'idle';
        closeMessages();
        navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: { ...(state ?? {}), [MARKER]: true, [RETURN]: back } });
        navigate(href);
      },
    }),
    [navigate, closeMessages, location, state],
  );
}

/** Mounted once by the phone layout; draws nothing until the messages are opened. */
export function PhoneMessages() {
  const panel = useMessagesPanel();
  const history = useMessagesHistory();
  const [filter, setFilter] = useState<WebMessageFilter>('all');
  // Each opening picks its filter before the screen draws: a banner's message shows under All,
  // the way back from a notice's page under the filter the client left.
  const [wasOpen, setWasOpen] = useState(false);
  if (panel.open !== wasOpen) {
    setWasOpen(panel.open);
    const next = panel.open ? (panel.atEntryId ? 'all' : panel.filter ?? filter) : filter;
    if (next !== filter) setFilter(next);
  }
  if (!panel.open) return null;
  return (
    <MessagesScreen
      filter={filter}
      onFilter={setFilter}
      restore={panel.restore && panel.restore.lens === filter ? panel.restore : null}
      focusId={panel.atEntryId}
      onBack={history.back}
      openPage={history.openPage}
    />
  );
}
